/**
 * useCrawlConfirmation — manages crawl-result confirmation state for the
 * DemoConversionBanner.  Extracted to keep the banner component under the
 * 400-line limit.
 */
import { useState, useCallback, useEffect } from 'react';
import { post } from '../../../utils/apiClient';
import type { WebsiteCrawlResult } from '../../setup-wizard/types';
import { buildCrawlPayload } from '../../setup-wizard/utils/buildCrawlPayload';

export interface CrawlConfirmationState {
  crawlResult: WebsiteCrawlResult | null;
  showCrawlConfirmation: boolean;
  applyingCrawl: boolean;
  /** True after the user has confirmed or skipped crawl data. */
  showSuccess: boolean;
  /** Whether the user confirmed (true) or skipped (false). */
  didConfirm: boolean;
  /** Index of the first wizard step NOT filled by crawl data (0 if skipped). */
  firstIncompleteStep: number;
  /** Call after a successful claim-org response that includes crawl_data. */
  setCrawlFromResponse: (data: Record<string, unknown>) => boolean;
  /** User confirmed — apply crawl data (possibly edited) to the org then reload. */
  handleConfirmCrawl: (editedData: WebsiteCrawlResult) => Promise<void>;
  /** User skipped — discard crawl data and reload. */
  handleSkipCrawl: () => void;
  /** Dismiss the success dialog (called by the two choice buttons). */
  dismissSuccess: () => void;
  /** Trigger the success dialog for the no-crawl path (manual claim). */
  triggerSuccessNoCrawl: () => void;
}

export default function useCrawlConfirmation(): CrawlConfirmationState {
  const [crawlResult, setCrawlResult] = useState<WebsiteCrawlResult | null>(null);
  const [showCrawlConfirmation, setShowCrawlConfirmation] = useState(false);
  const [applyingCrawl, setApplyingCrawl] = useState(false);
  // Restore success dialog state from sessionStorage if it was persisted
  // across an auth refresh (which unmounts the component tree).
  const [showSuccess, setShowSuccess] = useState(() => {
    try {
      if (sessionStorage.getItem('show_claim_success') === '1') {
        sessionStorage.removeItem('show_claim_success');
        return true;
      }
    } catch { /* noop */ }
    return false;
  });
  const [didConfirm, setDidConfirm] = useState(false);
  const [firstIncompleteStep, setFirstIncompleteStep] = useState(0);

  // Persist showSuccess to sessionStorage so it survives auth refresh unmounts
  useEffect(() => {
    try {
      if (showSuccess) {
        sessionStorage.setItem('show_claim_success', '1');
      } else {
        sessionStorage.removeItem('show_claim_success');
      }
    } catch { /* noop */ }
  }, [showSuccess]);

  /**
   * Inspect the claim-org response.  If it contains crawl data, stash
   * it and return `true` (caller should NOT reload).  Otherwise return
   * `false` so the caller can reload immediately.
   */
  const setCrawlFromResponse = useCallback(
    (data: Record<string, unknown>): boolean => {
      const cd = data.crawl_data;
      if (cd && typeof cd === 'object' && Object.keys(cd as object).length > 0) {
        setCrawlResult(cd as WebsiteCrawlResult);
        setShowCrawlConfirmation(true);
        return true;
      }
      return false;
    },
    [],
  );

  const handleConfirmCrawl = useCallback(async (editedData: WebsiteCrawlResult) => {
    setApplyingCrawl(true);
    try {
      const res = await post(
        '/api/auth/apply-crawl-data',
        buildCrawlPayload(editedData),
        { credentials: 'include' },
      );
      try {
        const body = await res.json();
        if (typeof body.first_incomplete_step === 'number') {
          setFirstIncompleteStep(body.first_incomplete_step);
        }
      } catch { /* response may not be JSON */ }
      setDidConfirm(true);
    } catch {
      // Non-fatal — org is already created, crawl apply is best-effort.
      // Show the "skip" message since crawl data wasn't actually saved.
      setDidConfirm(false);
    } finally {
      setApplyingCrawl(false);
      setShowCrawlConfirmation(false);
      setCrawlResult(null);
      // Mark onboarding as already checked so the dashboard doesn't
      // immediately redirect to the setup wizard after reload.
      try { sessionStorage.setItem('onboarding_login_checked', 'true'); } catch { /* noop */ }
      setShowSuccess(true);
    }
  }, []);

  const handleSkipCrawl = useCallback(() => {
    setShowCrawlConfirmation(false);
    setCrawlResult(null);
    // Same flag as handleConfirmCrawl — prevent wizard redirect after reload.
    try { sessionStorage.setItem('onboarding_login_checked', 'true'); } catch { /* noop */ }
    setDidConfirm(false);
    setShowSuccess(true);
  }, []);

  const dismissSuccess = useCallback(() => {
    setShowSuccess(false);
    // Synchronously clear sessionStorage so the flag doesn't survive a
    // page reload triggered immediately after (e.g. handleExploreDemo).
    try { sessionStorage.removeItem('show_claim_success'); } catch { /* noop */ }
  }, []);

  const triggerSuccessNoCrawl = useCallback(() => {
    // Prevent wizard redirect after reload.
    try { sessionStorage.setItem('onboarding_login_checked', 'true'); } catch { /* noop */ }
    // Persist flag so it survives the refreshAuth unmount/remount cycle.
    try { sessionStorage.setItem('show_claim_success', '1'); } catch { /* noop */ }
    setDidConfirm(false);
    setShowSuccess(true);
  }, []);

  return {
    crawlResult,
    showCrawlConfirmation,
    applyingCrawl,
    showSuccess,
    didConfirm,
    firstIncompleteStep,
    setCrawlFromResponse,
    handleConfirmCrawl,
    handleSkipCrawl,
    dismissSuccess,
    triggerSuccessNoCrawl,
  };
}
